import { useGetActivationAdminsQuery } from 'api/recoveryAi'
import type { PreparerAdmin } from 'api/recoveryAi.types'
import { useMemo } from 'react'

export type PreparerAdminsState = 'loading' | 'ready' | 'unavailable'

// The admins a preparer can hand activation off to (AI-923).
//
// Server-sourced from GET recovery/activation_requests — RBAC-eligible admins
// carrying an authoritative notifiedAt, replacing the earlier client-side
// merchant_users-filtered sourcing. The endpoint is flag-gated and 404s while
// dark, so ANY query error (a 404 off-flag, an RBAC 403, a network failure) reads
// as 'unavailable' — there is nothing to show, and the LD flag is the one real
// rollout switch, not a client-side capability check.
export const usePreparerAdmins = ({ skip = false }: { skip?: boolean } = {}): {
  admins: PreparerAdmin[]
  state: PreparerAdminsState
} => {
  const { data, isLoading, error } = useGetActivationAdminsQuery(undefined, {
    skip,
  })

  const admins = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  )

  const state: PreparerAdminsState = (() => {
    // A skipped fetch is "not needed here", not "broken" — but there is nothing to
    // show either, so report it as loading rather than a false 'ready' with 0 rows.
    if (skip) return 'loading'
    if (error) return 'unavailable'
    // RTK Query dispatches `initiate` from a useEffect, so the render where
    // `skip` flips true -> false still sees the pre-fetch state (isLoading:
    // false, data: undefined) for one frame. Treat "no data yet" as loading,
    // not a false 'ready' with 0 rows (Darshan, PR #4012).
    if (isLoading || !data) return 'loading'
    return 'ready'
  })()

  return { admins, state }
}

export default usePreparerAdmins
